<template>
  <v-dialog v-model="show" width="50vw" scrollable persistent>
    <v-card
      height="auto"
      elevation="5"
      class="rounded-lg"
      min-height="200"
      v-if="product"
    >
      <v-card-title class="text-subtitle-1 font-weight-bold">
        <v-row dense>
          <v-col>
            {{ product.name }}
            <v-chip
              small
              class="ml-2"
              dark
              :color="getProductTypeColor(product.type)"
            >
              {{ getProductTypeLabel(product.type) }}
            </v-chip>
            <v-chip
              v-if="product.isBiological"
              small
              class="ml-1"
              color="success"
            >
              Biológico
            </v-chip>
          </v-col>
        </v-row>
      </v-card-title>

      <v-card-subtitle class="text-caption">
        <div v-if="product.composition" class="mt-2">
          <strong>Composición:</strong>
          {{ product.composition }}
        </div>
      </v-card-subtitle>

      <v-divider></v-divider>

      <v-card-text class="py-2">
        <div
          v-if="
            product.isBiological &&
            product.microorganism &&
            product.microorganism.length
          "
        >
          <div class="text-caption font-weight-bold mb-1">Microorganismos:</div>
          <v-chip
            v-for="(mo, moIndex) in product.microorganism"
            :key="`mo-${moIndex}`"
            small
            class="mr-1 mb-1"
            color="primary"
          >
            {{ mo.name }} ({{ mo.concentration }})
          </v-chip>
        </div>

        <div
          v-if="
            !product.isBiological &&
            product.activeIngredients &&
            product.activeIngredients.length
          "
        >
          <div class="text-caption font-weight-bold mb-1">
            Ingredientes activos:
          </div>
          <v-chip
            v-for="(ai, aiIndex) in product.activeIngredients"
            :key="`ai-${aiIndex}`"
            small
            class="mr-1 mb-1"
            color="primary"
          >
            {{ ai.name }} ({{ ai.concentration }})
          </v-chip>
        </div>

        <div v-if="product.benefits && product.benefits.length" class="mt-2">
          <div class="text-caption font-weight-bold mb-1">Beneficios:</div>
          <v-chip
            v-for="(benefit, benefitIndex) in product.benefits"
            :key="`benefit-${benefitIndex}`"
            small
            class="mr-1 mb-1"
            color="secondary"
          >
            {{ benefit }}
          </v-chip>
        </div>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="px-3 py-1">
        <v-btn
          small
          text
          color="primary"
          :disabled="!product.technicalSheet"
          @click="openTechnicalSheet(product)"
        >
          Ficha Técnica
        </v-btn>

        <v-spacer></v-spacer>

        <v-btn small icon text @click="toggleDetails()">
          <v-icon>
            {{ showDetails ? "mdi-chevron-up" : "mdi-chevron-down" }}
          </v-icon>
        </v-btn>
      </v-card-actions>

      <v-expand-transition>
        <div v-if="showDetails">
          <v-divider></v-divider>
          <v-card-text class="text-caption pt-2 pb-1">
            <div v-if="product.crops && product.crops.length">
              <div class="font-weight-bold mb-1">Cultivos:</div>
              <div style="max-height: 400px; overflow-y: auto">
                <v-simple-table dense class="bg-transparent">
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th>Nombre de cultivo</th>
                        <th>Dosis</th>
                        <th>Recomendación</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(crop, cropIndex) in product.crops"
                        :key="`crop-${cropIndex}`"
                      >
                        <td>
                          <div v-for="(name, index) in crop.names" :key="index">
                            <span v-if="index == crop.names.length - 1">
                              {{ getCrop(name) || name }}
                            </span>
                            <span v-else> {{ getCrop(name) || name }}, </span>
                          </div>
                        </td>
                        <td>{{ crop.doses }}</td>
                        <td>{{ crop.recommendation }}</td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </div>
            </div>
            <div
              v-if="product.safetyInterval || product.reEntryPeriod"
              class="mt-2"
            >
              <div class="font-weight-bold mb-1">Información de seguridad</div>
              <v-row dense>
                <v-col cols="6" v-if="product.safetyInterval">
                  <strong
                    >Intervalo de seguridad (entre última aplicación y la
                    cosecha):</strong
                  >
                  {{ product.safetyInterval }}
                </v-col>
                <v-col cols="6" v-if="product.reEntryPeriod">
                  <strong>Periodo de reingreso al lugar tratado:</strong>
                  {{ product.reEntryPeriod }}
                </v-col>
                <v-col cols="6" v-if="product.phytotoxicity">
                  <strong>Fitotoxicidad:</strong>
                  {{ product.phytotoxicity }}
                </v-col>
              </v-row>
            </div>
            <div
              v-if="
                product.incompatibilityWithOtherProducts || product.warnings
              "
              class="mt-2"
            >
              <div class="font-weight-bold mb-1">Información adicional:</div>
              <div v-if="product.incompatibilityWithOtherProducts">
                <strong>Incompatibilidad:</strong>
                {{ product.incompatibilityWithOtherProducts }}
              </div>
              <div v-if="product.warnings">
                <strong>Advertencias:</strong>
                {{ product.warnings }}
              </div>
              <div v-if="product.risks">
                <strong>Riesgos:</strong>
                {{ product.risks }}
              </div>
              <div v-if="product.storage">
                <strong>Almacenamiento:</strong>
                {{ product.storage }}
              </div>
            </div>
            <div v-if="product.observations" class="mt-2">
              <div class="font-weight-bold mb-1">Observaciones:</div>
              {{ product.observations }}
            </div>
          </v-card-text>
        </div>
      </v-expand-transition>

      <v-card-actions>
        <v-container>
          <v-row>
            <v-col>
              <v-btn
                color="gray"
                block
                @click="closeModal"
                dark
                :disabled="isLoading"
                >Cerrar</v-btn
              >
            </v-col>
          </v-row>
        </v-container>
      </v-card-actions>
    </v-card>
    <v-card v-if="!isLoading && !product">
      <v-card-text> No se logro cargar el producto </v-card-text>
    </v-card>
    <v-card v-if="isLoading">
      <v-card-text>
        <v-progress-circular
          :size="70"
          :width="7"
          color="primary"
          indeterminate
        ></v-progress-circular>
      </v-card-text>
    </v-card>
    <pdf-viewer
      v-model="showPdfViewer"
      v-if="showPdfViewer"
      :urlPreview="url_pdf"
      :actionButton="false"
      @close="clearUrl"
    />
  </v-dialog>
</template>

<script>
//vuex
import { mapState, mapMutations, mapActions } from "vuex";
import PdfViewer from "../../utils/PdfViewer.vue";
export default {
  components: { PdfViewer },
  data: () => ({
    isLoading: false,
    product: null,
    showPdfViewer: false,
    url_pdf: "",
    showDetails: false,
  }),
  props: {
    value: Boolean,
  },
  computed: {
    ...mapState("bioassays", ["productIdSelected"]),
    show: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      },
    },
  },
  async mounted() {
    await this.fetchProduct();
  },
  methods: {
    ...mapActions("bioassays", ["getProductById"]),
    ...mapMutations("alerts", ["SET_ALERT_TYPE"]),
    closeModal() {
      this.$emit("close");
    },
    async fetchProduct() {
      try {
        this.isLoading = true;
        let response = await this.getProductById({
          productId: this.productIdSelected,
        });
        this.product = response.product;
      } catch (error) {
        console.log(error);
        this.SET_ALERT_TYPE({
          visible: true,
          type: "error",
          text: `Algo salió mal. Error: ${error.message}`,
          timeout: 5000,
        });
      } finally {
        this.isLoading = false;
      }
    },
    getProductTypeColor(type) {
      const productTypes = [
        { value: "FERTILIZER", text: "Fertilizante", color: "blue" },
        {
          value: "BIOSTIMULANT",
          text: "Bioestimulante",
          color: "green",
        },
        {
          value: "PHYTOSANITARY",
          text: "Fitosanitario",
          color: "orange",
        },
        { value: "INOCULANT", text: "Inoculante", color: "purple" },
      ];
      const found = productTypes.find((t) => t.value === type);
      return found ? found.color : "grey";
    },
    getProductTypeLabel(type) {
      const productTypes = [
        { value: "FERTILIZER", text: "Fertilizante", color: "blue" },
        {
          value: "BIOSTIMULANT",
          text: "Bioestimulante",
          color: "green",
        },
        {
          value: "PHYTOSANITARY",
          text: "Fitosanitario",
          color: "orange",
        },
        { value: "INOCULANT", text: "Inoculante", color: "purple" },
      ];
      const found = productTypes.find((t) => t.value === type);
      return found ? found.text : "-";
    },
    openTechnicalSheet(product) {
      if (product.technicalSheet.includes("pdf")) {
        this.url_pdf = product.technicalSheet;
        this.showPdfViewer = true;
      } else {
        window.open(product.technicalSheetUrl, "_blank");
      }
    },
    clearUrl() {
      this.showPdfViewer = false;
      this.url_pdf = "";
    },
    toggleDetails() {
      this.showDetails = !this.showDetails;
    },
    getCrop(value) {
      let template = "";
      const crop = {
        olive: "Aceituna",
        chard: "Acelga",
        achiote: "Achiote",
        agave: "Agave",
        avocado: "Aguacate",
        garlic: "Ajo",
        sesame: "Ajonjolí",
        basil: "Albahaca",
        artichoke: "Alcachofa",
        alcatraz: "Alcatraz",
        shrunkenAlfalfa: "Alfalfa achicalada",
        greenAlfalfa: "Alfalfa verde",
        cotton: "Algodón",
        almond: "Almendro",
        birdseed: "Alpiste",
        amaranth: "Amaranto",
        anise: "Anís",
        celery: "Apio",
        blueberry: "Arándano",
        myrtle: "Arrayán",
        rice: "Arroz",
        greenForageOats: "Avena forrajera en verde",
        oatGrain: "Avena grano",
        bamboo: "Bambú",
        eggplant: "Berenjena",
        beetroot: "Betabel",
        broccoli: "Brocoli",
        peanut: "Cacahuate",
        cocoa: "Cacao",
        cherryCoffee: "Café cereza",
        pumpkin: "Calabaza",
        sweetPotato: "Camote",
        sugarCane: "Caña de azúcar",
        canola: "Canola",
        carambolo: "Carambolo",
        safflower: "Cártamo",
        barley: "Cebada",
        greenForageBarley: "Cebada forrajera en verde",
        onion: "Cebolla",
        chives: "Cebollín",
        ryeGrain: "Centeno grano",
        cherry: "Cereza",
        chabacano: "Chabacano",
        pea: "Chícharo",
        squash: "Chayote",
        chia: "Chía",
        chilacayote: "Chilacayote",
        chillyPepper: "Chile / Pimiento",
        custardApple: "Chirimoya",
        coriander: "Cilantro",
        plum: "Ciruela",
        coconutFruit: "Coco fruta",
        cabbage: "Col (repollo)",
        brusselsSprouts: "Col de Bruselas",
        cauliflower: "Coliflor",
        cumin: "Comino",
        chrysanthemum: "Crisantemo (planta)",
        peach: "Durazno",
        date: "Dátil",
        greenBean: "Ejote",
        cornCob: "Elote",
        dill: "Eneldo",
        epazote: "Epazote",
        asparagus: "Espárrago",
        spicesAndMedicinals: "Especias y medicinales",
        spinach: "Espinaca",
        raspberry: "Frambuesa",
        strawberry: "Fresa",
        bean: "Frijol",
        chickpea: "Garbanzo",
        sunflower: "Girasol",
        pomegranate: "Granada",
        soursop: "Guanábana",
        guava: "Guayaba",
        henequen: "Henequén",
        peppermint: "Hierbabuena",
        aromaticHerbs: "Hierbas aromáticas",
        fig: "Higo",
        jamaica: "Jamaica",
        ginger: "Jenjibre",
        jicama: "Jícama",
        lettuce: "Lechuga",
        lentil: "Lenteja",
        lime: "Lima",
        lemon: "Limón",
        linseed: "Linaza",
        litchi: "Litchi",
        corn: "Maíz",
        macadamia: "Macadamia",
        maguey: "Maguey",
        mamey: "Mamey",
        tangerine: "Mandarina",
        mango: "Mango",
        mangosteen: "Mangostán",
        chamomile: "Manzanilla",
        appleTree: "Manzano",
        passionFruit: "Maracuyá",
        daisyFlower: "Margarita",
        marjoram: "Mejorana",
        melon: "Melón",
        quince: "Membrillo",
        mint: "Menta",
        moringa: "Moringa",
        turnip: "Nabo",
        nanche: "Nanche",
        napa: "Napa",
        orange: "Naranja",
        medlar: "Níspero",
        nochebuena: "Nochebuena (planta)",
        walnut: "Nogal",
        noni: "Noni",
        nopal: "Nopal",
        oregano: "Orégano",
        ornamental: "Ornamentales",
        orchid: "Orquídea (planta)",
        oilPalm: "Palma africana o de aceite",
        potato: "Papa",
        papaya: "Papaya",
        grasslands: "Pastizales",
        matGrass: "Pasto tapete (m2)",
        pasturesAndMeadows: "Pastos y praderas",
        cucumber: "Pepino",
        pearTree: "Peral",
        parsley: "Perejil",
        peron: "Perón",
        persimony: "Persimonio",
        pepper: "Pimienta",
        pineapple: "Piña",
        pinion: "Piñón",
        pistache: "Pistache",
        pitaya: "Pitaya",
        banana: "Plátano",
        pore: "Poro",
        radish: "Rábano",
        rambutan: "Rambután",
        romerito: "Romerito",
        rosemary: "Romero",
        aloe: "Sábila",
        watermelon: "Sandía",
        sorghum: "Sorgo",
        soy: "Soya",
        stevia: "Stevia",
        tobacco: "Tabaco",
        tamarind: "Tamarindo",
        tejocote: "Tejocote",
        tomato: "Tomate Rojo (jitomate)",
        greenTomato: "Tomate Verde",
        thyme: "Tomillo",
        grapefruit: "Toronja (pomelo)",
        clover: "Trébol",
        wheat: "Trigo",
        dutchTulip: "Tulipán holandés",
        tuna: "Tuna",
        grape: "Uva",
        vanilla: "Vainilla",
        purslane: "Verdolaga",
        vine: "Vid",
        cassavaFood: "Yuca alimenticia",
        carrot: "Zanahoria",
        sapodilla: "Zapote",
        blackberry: "Zarzamora",
        zempoalxochitl: "Zempoalxochitl",
        fallow: "Barbecho",
        other: "Otro",
      };
      let cropName = crop[value];
      if (cropName) {
        template = cropName;
      } else {
        template = "Sin cultivo";
      }
      return template;
    },
  },
};
</script>
